import {
  mergeMessengerV2ReceiptFrontiers,
  messengerV2ReceiptCovers,
} from './receiptFrontier.js';

export { mergeMessengerV2ReceiptFrontiers, messengerV2ReceiptCovers } from './receiptFrontier.js';

const DEFAULT_RETRY_DELAYS_MS = [1000, 3000, 10000];

const toStringOrNull = (value) => {
  if (value == null) return null;
  const normalized = String(value).trim();
  return normalized || null;
};

const shouldRetryReceiptError = (error) => {
  const status = Number(error?.status);
  return !Number.isFinite(status) || status === 408 || status === 429 || status >= 500;
};

export const createMessengerV2ReadReceiptCoordinator = ({
  publishReceipt,
  retryDelaysMs = DEFAULT_RETRY_DELAYS_MS,
  schedule = (callback, delay) => window.setTimeout(callback, delay),
  cancel = (timerId) => window.clearTimeout(timerId),
} = {}) => {
  if (typeof publishReceipt !== 'function') {
    throw new Error('publishReceipt is required');
  }

  const entries = new Map();

  const settleCoveredWaiters = (entry) => {
    const pending = [];
    entry.waiters.forEach((waiter) => {
      if (messengerV2ReceiptCovers(entry.acknowledged, waiter.target)) {
        waiter.resolve(entry.acknowledged);
      } else {
        pending.push(waiter);
      }
    });
    entry.waiters = pending;
  };

  const rejectWaiters = (entry, error) => {
    entry.waiters.splice(0).forEach((waiter) => waiter.reject(error));
  };

  const scheduleRetry = (conversationId, entry, error) => {
    const delay = retryDelaysMs[entry.attempt];
    entry.attempt += 1;
    if (delay == null) {
      rejectWaiters(entry, error);
      entries.delete(conversationId);
      return;
    }

    entry.retryTimer = schedule(() => {
      entry.retryTimer = null;
      void drain(conversationId);
    }, delay);
  };

  const drain = async (conversationId) => {
    const entry = entries.get(conversationId);
    if (!entry || entry.inFlight || entry.retryTimer || entry.waiters.length === 0) return;

    entry.inFlight = true;
    const target = entry.desired;
    try {
      const { unreadCount: _unreadCount, ...request } = target;
      const acknowledged = await publishReceipt(request);
      entry.acknowledged = mergeMessengerV2ReceiptFrontiers(
        entry.acknowledged,
        acknowledged || target,
      );
      const targetAcknowledged = messengerV2ReceiptCovers(entry.acknowledged, target);
      settleCoveredWaiters(entry);
      entry.inFlight = false;

      if (entry.waiters.length === 0) {
        entries.delete(conversationId);
        return;
      }

      if (targetAcknowledged) {
        entry.attempt = 0;
        void drain(conversationId);
      } else {
        scheduleRetry(
          conversationId,
          entry,
          new Error('Read receipt acknowledgement did not reach the requested frontier'),
        );
      }
    } catch (error) {
      entry.inFlight = false;
      if (shouldRetryReceiptError(error)) {
        scheduleRetry(conversationId, entry, error);
      } else {
        rejectWaiters(entry, error);
        entries.delete(conversationId);
      }
    }
  };

  const publish = (receipt = null) => {
    const conversationId = toStringOrNull(receipt?.conversationId);
    if (!conversationId) {
      return Promise.reject(new Error('conversationId is required'));
    }

    const target = mergeMessengerV2ReceiptFrontiers(null, {
      ...receipt,
      conversationId,
    });
    const entry = entries.get(conversationId) || {
      desired: null,
      acknowledged: null,
      waiters: [],
      inFlight: false,
      retryTimer: null,
      attempt: 0,
    };
    entry.desired = mergeMessengerV2ReceiptFrontiers(entry.desired, target);
    entries.set(conversationId, entry);

    const promise = new Promise((resolve, reject) => {
      entry.waiters.push({ target, resolve, reject });
    });
    void drain(conversationId);
    return promise;
  };

  const clear = () => {
    const error = new Error('Read receipt coordinator was cleared');
    entries.forEach((entry) => {
      if (entry.retryTimer) cancel(entry.retryTimer);
      rejectWaiters(entry, error);
    });
    entries.clear();
  };

  return {
    clear,
    publish,
  };
};

export default createMessengerV2ReadReceiptCoordinator;
